import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Seo, { SITE } from '../../components/Seo/Seo';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faPlus,
	faXmark,
	faCircleCheck,
	faArrowLeft,
	faArrowRight,
	faCalendar,
	faClock,
	faCircle,
	faCalendarCheck,
	faStar,
	faComment,
} from '@fortawesome/pro-regular-svg-icons';
import {
	faCheck,
	faMusic,
	faCircleCheck as faCircleCheckSolid,
} from '@fortawesome/pro-solid-svg-icons';

import {
	TUITION_SEASON_TOTAL_LABEL,
	TUITION_MONTHLY_LABEL,
	TUITION_INSTALLMENTS,
	TUITION_SCHEDULE_LABEL,
	FIRST_LESSON_BY_WEEKDAY,
	formatSeasonDate,
} from '../../data/seasonData';
import { submitInquiry } from '../../utils/inquiryService';
import {
	getOpenRegistrationSlots,
	submitRegistrationRequest,
} from '../../utils/registrationService';
import { emailRe, friendlyServerError } from '../../utils/formValidation';
import { trackLead } from '../../utils/metaPixel';
import { instrumentDisplayLabel } from '../../utils/packService';
import { formatTime12Hour, getDayName } from '../../utils/schedulingUtils';
import './RegisterStudent.css';

// The season's first Monday lesson, from canon rather than typed out. This page
// showed it in THREE places, each spelling the date by hand — correct on the day
// they were written, and silently wrong the next season. /fall reads the same
// canon field, which is exactly how two pages come to quote two different start
// dates. Two shapes because the sidebar row reads as a sentence and does not
// want the year.
const firstFallLessonLabel = formatSeasonDate(FIRST_LESSON_BY_WEEKDAY.mon);
const firstFallLessonShort = formatSeasonDate(FIRST_LESSON_BY_WEEKDAY.mon, {
	year: undefined,
});

const PRONOUNS = ['she/her', 'he/him', 'they/them', 'Prefer not to say'];
const LESSON_INTERESTS = ['Voice', 'Piano', 'Guitar', 'Acting', 'Not sure yet'];
// Official age floor is 8; younger families go through the visible "Under 8"
// case-by-case path (6–7 accepted, under 6 refused) instead of a picker that
// silently stops at 8.
const AGE_OPTIONS = Array.from({ length: 11 }, (_, i) => `${i + 8}`);
const LOCATIONS = ['No preference', 'Lakeville area', 'Burnsville (Headline Dance Center)'];
const HEARD_OPTIONS = [
	'Friend or family referral',
	'Current CYV family',
	'School or teacher',
	'Google or web search',
	'Social media',
	'Other',
];
// These referral-type answers reveal the optional "Who should we thank?" field.
const THANK_TRIGGERS = ['Friend or family referral', 'Current CYV family', 'School or teacher'];

// Slot locations are the canonical enum (LAKEVILLE | HLDC); the friendly
// label is display-only.
const SLOT_LOCATION_LABELS = {
	Lakeville: 'Lakeville',
	HLDC: 'Burnsville (Headline Dance Center)',
};
const slotLocationLabel = (loc) => SLOT_LOCATION_LABELS[loc] || loc;
// City-only on the availability picker (chips + filter): the full label
// overflows the chips, and the venue name (HLDC) deliberately stays off the
// picker — location descriptor, never a co-brand; the venue/address surfaces
// at a later warm touchpoint (confirmation/claim/welcome).
const SLOT_LOCATION_SHORT = {
	Lakeville: 'Lakeville',
	HLDC: 'Burnsville',
};
const slotLocationShort = (loc) => SLOT_LOCATION_SHORT[loc] || loc;
const describeSlot = (slot) =>
	`${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} — ${slotLocationLabel(slot.location)}`;

const emptyStudent = () => ({
	firstName: '',
	lastName: '',
	age: '',
	underEight: false,
	pronouns: '',
	school: '',
	lessonInterests: [],
	locationPreference: '',
	returning: false,
	// Fall request-and-assign: which open slots work for this child, plus the
	// two escape hatches. A request is NOT a booking.
	requestedSlotIds: [],
	keepLastYearTime: false,
	openToAnything: false,
});

const RegisterStudent = () => {
	const [students, setStudents] = useState([emptyStudent()]);
	const [parent, setParent] = useState({ firstName: '', lastName: '', email: '', phone: '', address: '' });
	const [referralSource, setReferralSource] = useState('');
	const [referredBy, setReferredBy] = useState('');
	const [notes, setNotes] = useState('');
	// Step 2's schedule-specific notes (the Design template's card). Kept
	// separate from step 1's "Anything else?" and merged into the single
	// `notes` payload field at submit — no backend change.
	const [scheduleNotes, setScheduleNotes] = useState('');
	const [acknowledged, setAcknowledged] = useState(false);
	const [companyWebsite, setCompanyWebsite] = useState(''); // honeypot
	// Steps: 1 = your info · 2 = pick times (the request) · 3 = review.
	const [step, setStep] = useState(1);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [done, setDone] = useState(false);
	// Progressive capture: the lead is written when the family advances past
	// step 1 (via the existing submitInquiry), so a visitor who bails before
	// picking times is still a contactable inquiry. Guarded to fire once.
	const [leadCaptured, setLeadCaptured] = useState(false);
	// The open fall pool (projected: id/day/time/duration/location only).
	const [slots, setSlots] = useState([]);
	const [slotsLoading, setSlotsLoading] = useState(false);
	const [slotsError, setSlotsError] = useState('');
	const [slotFilter, setSlotFilter] = useState('all'); // 'all' | 'Lakeville' | 'HLDC'
	// Step-2 attention state: flips on the first failed Continue and marks
	// every child card still missing a time answer (ring + sharpened copy),
	// scrolling the first one into view. Per-card marks clear themselves the
	// moment that child is covered. QA walkthrough finding 2026-08-17: a
	// first-time user scrolled past the times question and the disabled
	// Continue gave her nothing at the moment she was stuck.
	const [timesAttn, setTimesAttn] = useState(false);
	const timesCardRefs = useRef([]);

	const showThank = THANK_TRIGGERS.includes(referralSource);

	const setStudentField = (i, field) => (e) => {
		const v = e.target.value;
		setStudents((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)));
	};
	// Age dropdown: a real age (8–18) or 'under8', which clears the age and
	// reveals the case-by-case number input.
	const setAgeChoice = (i) => (e) => {
		const v = e.target.value;
		setStudents((prev) =>
			prev.map((s, idx) =>
				idx === i ? { ...s, underEight: v === 'under8', age: v === 'under8' ? '' : v } : s
			)
		);
	};
	const toggleInterest = (i, option) => {
		setStudents((prev) =>
			prev.map((s, idx) => {
				if (idx !== i) return s;
				const has = s.lessonInterests.includes(option);
				return {
					...s,
					lessonInterests: has
						? s.lessonInterests.filter((o) => o !== option)
						: [...s.lessonInterests, option],
				};
			})
		);
	};
	const addStudent = () => setStudents((prev) => [...prev, emptyStudent()]);
	const removeStudent = (i) => setStudents((prev) => prev.filter((_, idx) => idx !== i));
	const setP = (field) => (e) => setParent((prev) => ({ ...prev, [field]: e.target.value }));

	const fullName = (first, last) => `${first.trim()} ${last.trim()}`.trim();

	const validateStep1 = () => {
		if (!parent.firstName.trim() || !parent.lastName.trim())
			return 'Please add the parent or guardian first and last name.';
		if (!emailRe.test(parent.email.trim())) return 'Please enter a valid email address.';
		if (!parent.phone.trim()) return 'Please enter a phone number so we can reach you.';
		for (let i = 0; i < students.length; i++) {
			const s = students[i];
			const who = students.length > 1 ? `student ${i + 1}` : 'your young artist';
			if (!s.firstName.trim() || !s.lastName.trim()) return `Please add a first and last name for ${who}.`;
			if (!`${s.age}`.trim()) return `Please add an age for ${who}.`;
			if (s.underEight) {
				const n = parseInt(`${s.age}`.trim(), 10);
				if (Number.isNaN(n) || n < 6)
					return `We aren't able to offer lessons for children under 6 just yet — thanks for thinking of us!`;
				if (n >= 8) return `Looks like ${who} is 8 or older — please pick their age from the list.`;
			}
		}
		if (!acknowledged) return "Please confirm you've read our policies to continue.";
		return '';
	};

	const loadSlots = async () => {
		setSlotsLoading(true);
		setSlotsError('');
		try {
			setSlots(await getOpenRegistrationSlots());
		} catch (err) {
			console.error('Failed to load open slots:', err);
			setSlotsError('We had trouble loading the current openings. Please try again.');
		} finally {
			setSlotsLoading(false);
		}
	};

	// Step 1 → 2. Writes the lead FIRST (progressive capture): if the family
	// never finishes picking times, we can still reach out. submitInquiry is
	// the existing untouched callable; the final submit links this lead by
	// email server-side.
	//
	// `stage` tells the server this is a MID-FORM capture, not a finished
	// submission, which suppresses the parent confirmation email. Without it the
	// family gets "Thank you for registering! ... we'll take it from here" while
	// still sitting on step 2 — which reads as terminal, and demonstrably is:
	// Summer Langer got that mail sixty seconds in on 2026-08-20 and stopped, and
	// Tami Johnson only got through by emailing her times separately. The
	// confirmation now comes from submitRegistrationRequest at step 3. Dropping
	// this one field silently restores the old behaviour, so it is test-pinned.
	const goPickTimes = async (e) => {
		e.preventDefault();
		setError('');
		const v = validateStep1();
		if (v) {
			setError(v);
			return;
		}
		if (!leadCaptured) {
			setSubmitting(true);
			try {
				await submitInquiry({
					parent: {
						name: fullName(parent.firstName, parent.lastName),
						email: parent.email.trim(),
						phone: parent.phone.trim(),
						address: parent.address.trim() || null,
					},
					students: students.map((s) => ({
						name: fullName(s.firstName, s.lastName),
						age: `${s.age}`.trim(),
						pronouns: s.pronouns || null,
						school: s.school.trim() || null,
						lessonInterest: s.lessonInterests.length > 0 ? s.lessonInterests : null,
						locationPreference: s.locationPreference || null,
					})),
					referralSource: referralSource || null,
					referredBy: showThank ? referredBy.trim() || null : null,
					notes: notes.trim() || null,
					acknowledgedPolicies: acknowledged,
					stage: 'register-step1',
					companyWebsite, // honeypot — server silently drops if filled
				});
				// Meta Pixel Lead — fired when the lead is actually captured
				// (step 1), not at the final request submit.
				trackLead();
				setLeadCaptured(true);
			} catch (err) {
				console.error('Inquiry submit failed:', err);
				setError(friendlyServerError(err, 'Something went wrong saving your info. Please try again.'));
				setSubmitting(false);
				return;
			}
			setSubmitting(false);
		}
		setStep(2);
		loadSlots();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const toggleSlot = (i, slotId) => {
		setStudents((prev) =>
			prev.map((s, idx) => {
				if (idx !== i) return s;
				const has = s.requestedSlotIds.includes(slotId);
				return {
					...s,
					requestedSlotIds: has
						? s.requestedSlotIds.filter((id) => id !== slotId)
						: [...s.requestedSlotIds, slotId],
				};
			})
		);
	};

	const toggleChildFlag = (i, field) => {
		setStudents((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: !s[field] } : s)));
	};

	// Step 1 returning flag. Un-flagging also clears a stale keepLastYearTime
	// so a family that toggles returning off after visiting step 2 can't carry
	// a hidden "keep my time" through validation and submit.
	const toggleReturning = (i) => {
		setStudents((prev) =>
			prev.map((s, idx) =>
				idx === i
					? { ...s, returning: !s.returning, keepLastYearTime: !s.returning ? s.keepLastYearTime : false }
					: s
			)
		);
	};

	// A child is "covered" by ≥1 requested slot or an escape hatch; the
	// keep-my-time hatch only counts when the family flagged returning on
	// step 1 (the hatch only renders then, too). Note the branches are not
	// mutually exclusive in the data — a real request can carry both picked
	// slots AND openToAnything, and both must stay expressible.
	const childCovered = (s) =>
		s.requestedSlotIds.length > 0 || (s.returning && s.keepLastYearTime) || s.openToAnything;

	const validateTimes = () => {
		for (let i = 0; i < students.length; i++) {
			const s = students[i];
			const who =
				students.length > 1 ? s.firstName.trim() || `student ${i + 1}` : 'your young artist';
			if (!childCovered(s)) {
				return `Please pick at least one time that works for ${who} — or check "open to anything."`;
			}
		}
		return '';
	};

	const goReview = () => {
		setError('');
		const v = validateTimes();
		if (v) {
			setError(v);
			// Moment-of-need feedback: the Continue button is deliberately
			// ENABLED while incomplete — a disabled control gives a first-time
			// user nothing at the exact moment they're stuck. An invalid click
			// marks the incomplete child cards and scrolls the first one into
			// view instead.
			setTimesAttn(true);
			const firstIdx = students.findIndex((s) => !childCovered(s));
			timesCardRefs.current[firstIdx]?.scrollIntoView?.({
				behavior: 'smooth',
				block: 'center',
			});
			return;
		}
		setStep(3);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleConfirm = async () => {
		setError('');
		setSubmitting(true);
		// One `notes` field server-side: step 1's general note + step 2's
		// schedule note travel together, labeled so Grace can tell them apart.
		const mergedNotes =
			[
				notes.trim(),
				scheduleNotes.trim() ? `Schedule: ${scheduleNotes.trim()}` : '',
			]
				.filter(Boolean)
				.join('\n') || null;
		try {
			await submitRegistrationRequest({
				parent: {
					name: fullName(parent.firstName, parent.lastName),
					email: parent.email.trim(),
					phone: parent.phone.trim(),
					address: parent.address.trim() || null,
				},
				children: students.map((s) => ({
					childName: fullName(s.firstName, s.lastName),
					age: `${s.age}`.trim(),
					pronouns: s.pronouns || null,
					school: s.school.trim() || null,
					instrument: s.lessonInterests.length > 0 ? s.lessonInterests : null,
					locationPref: s.locationPreference || null,
					returning: s.returning,
					keepLastYearTime: s.returning && s.keepLastYearTime,
					openToAnything: s.openToAnything,
					requestedSlotIds: s.requestedSlotIds,
				})),
				referralSource: referralSource || null,
				referredBy: showThank ? referredBy.trim() || null : null,
				notes: mergedNotes,
				acknowledgedPolicies: acknowledged,
				companyWebsite, // honeypot — server silently drops if filled
			});
			setDone(true);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} catch (err) {
			console.error('Registration request failed:', err);
			setError(friendlyServerError(err, 'Something went wrong sending your request. Please try again.'));
			setSubmitting(false);
		}
	};

	// Shared 3-step indicator (Your details → Times that work → Review & send).
	// Labels + connector lines per the RegisterAvailability design template;
	// non-active labels hide at phone widths (CSS).
	const stepsIndicator = (current) => {
		const STEP_LABELS = ['Your details', 'Times that work', 'Review & send'];
		return (
			<div className='register-steps' aria-hidden='true'>
				{STEP_LABELS.map((label, idx) => {
					const n = idx + 1;
					const cls =
						n < current
							? 'register-step register-step--done'
							: n === current
							? 'register-step register-step--active'
							: 'register-step';
					return (
						<React.Fragment key={label}>
							{idx > 0 && <span className='register-step__line' />}
							<span className={cls}>
								<span className='register-step__num'>
									{n < current ? <FontAwesomeIcon icon={faCheck} /> : n}
								</span>
								<span
									className={`register-step__lbl${
										n === current ? '' : ' register-step__lbl--off'
									}`}>
									{label}
								</span>
							</span>
						</React.Fragment>
					);
				})}
			</div>
		);
	};

	const selectField = (label, value, onChange, options, optional = true) => (
		<div className='register-field'>
			<label className='register-label'>
				{label} {optional && <span className='register-opt'>(optional)</span>}
			</label>
			<select className='register-select' value={value} onChange={onChange}>
				<option value=''>Select…</option>
				{options.map((o) => (
					<option key={o} value={o}>
						{instrumentDisplayLabel(o)}
					</option>
				))}
			</select>
		</div>
	);

	// ── Success ──
	if (done) {
		return (
			<main className='register-page'>
				<Helmet defer={false}>
					<title>Registration received | Color Your Voice</title>
				</Helmet>
				<div className='register-card'>
					<div className='register-inner register-success'>
						<FontAwesomeIcon icon={faCircleCheck} className='register-success__icon' />
						<h1 className='register-success__title'>We've got your request — we'll be in touch soon.</h1>
						<p className='register-success__body'>
							Thanks for registering with Color Your Voice. We've saved the times that
							work for your family, and a member of our team will reach out to confirm
							your lesson time. There's nothing to pay today — keep an eye on your
							inbox.
						</p>
						<Link to='/' className='btn-cyv-teal'>
							Back to home
						</Link>
					</div>
				</div>
			</main>
		);
	}

	// ── Pick times (step 2) — the REQUEST. Checking a time never books it.
	// Layout + copy per the RegisterAvailability design template (two-column:
	// form + context sidebar, matching the .co- checkout pattern). ──
	if (step === 2) {
		const visibleSlots =
			slotFilter === 'all' ? slots : slots.filter((s) => s.location === slotFilter);
		const slotsByDay = visibleSlots.reduce((acc, s) => {
			(acc[s.dayOfWeek] = acc[s.dayOfWeek] || []).push(s);
			return acc;
		}, {});
		const dayOrder = Object.keys(slotsByDay)
			.map(Number)
			.sort((a, b) => a - b);

		// Coverage logic lives in childCovered (component scope) so the
		// validator, this gate, and the attention marks can't drift apart.
		const allOk = students.every(childCovered);

		const hatchButton = (on, onClick, label, sub) => (
			<button
				type='button'
				className={`rg-hatch${on ? ' rg-hatch--on' : ''}`}
				onClick={onClick}
				aria-pressed={on}>
				<span className='rg-hbox'>
					<FontAwesomeIcon icon={faCheck} />
				</span>
				<span className='rg-hatch__txt'>
					<span className='rg-hatch__label'>{label}</span>
					<span className='rg-hatch__sub'>{sub}</span>
				</span>
			</button>
		);

		return (
			<main className='register-page register-page--avail cyv'>
				<Helmet defer={false}>
					<title>Pick lesson times | Color Your Voice</title>
				</Helmet>

				{stepsIndicator(2)}

				<header className='rg-head'>
					<span className='eyebrow'>Step 2 of 3</span>
					<h1 className='rg-title'>Which times could work?</h1>
					<p className='rg-sub'>
						These are our open <strong>weekly school-year lesson times</strong>. Pick
						every time that could work — the more you choose, the easier it is for us
						to find a great fit.
					</p>
					<div className='rg-pills'>
						<span className='rg-pill'>
							<FontAwesomeIcon icon={faCalendar} className='rg-pill__i rg-pill__i--orange' />
							First fall lesson: {firstFallLessonLabel}
						</span>
						<span className='rg-pill'>
							<FontAwesomeIcon icon={faClock} className='rg-pill__i rg-pill__i--teal' />
							One 45-minute lesson each week
						</span>
					</div>
				</header>

				{/* Compact context card — phones only (hidden ≥941px, where the
				    sticky sidebar carries the same info). Price + start date must
				    never sit below the Continue CTA on mobile. */}
				<div className='rg-ctxcard cyv-card' style={{ '--accent': 'var(--grad-lessons)' }}>
					<span className='accent-bar' />
					{/* Year-first here too — same decision as the sidebar card. */}
					<div className='rg-ctx__price'>
						<span className='rg-ctx__amt'>{TUITION_SEASON_TOTAL_LABEL}</span>
						<span className='rg-ctx__per'>
							/ school year · {TUITION_INSTALLMENTS} monthly payments of{' '}
							{TUITION_MONTHLY_LABEL}
						</span>
					</div>
					<div className='rg-ctx__list'>
						<span className='rg-ctx__row'>
							<FontAwesomeIcon icon={faCalendar} className='rg-ctx__i rg-pill__i--orange' />
							First fall lesson: {firstFallLessonLabel}
						</span>
						<span className='rg-ctx__row'>
							<FontAwesomeIcon icon={faClock} className='rg-ctx__i rg-pill__i--teal' />
							One 45-minute lesson each week, all year
						</span>
					</div>
					<p className='rg-ctx__note'>Full program details are below the form.</p>
				</div>

				<div className='rg-grid'>
					<section>
						{error && (
							<Alert variant='danger' className='register-error'>
								{error}
							</Alert>
						)}

						{slotsLoading ? (
							<p className='register-slots-status'>Loading open times…</p>
						) : slotsError ? (
							<Alert variant='danger' className='register-error'>
								{slotsError}{' '}
								<button type='button' className='register-retry' onClick={loadSlots}>
									Try again
								</button>
							</Alert>
						) : (
							<>
								{slots.length > 0 && (
									<div className='rg-fchips' role='group' aria-label='Filter by location'>
										{[
											{ key: 'all', label: 'All locations' },
											{ key: 'Lakeville', label: 'Lakeville' },
											{ key: 'HLDC', label: 'Burnsville' },
										].map((f) => (
											<button
												key={f.key}
												type='button'
												className={`rg-fchip${slotFilter === f.key ? ' rg-fchip--on' : ''}`}
												onClick={() => setSlotFilter(f.key)}>
												{f.label}
											</button>
										))}
									</div>
								)}

								{students.map((s, i) => {
									const first = s.firstName.trim() || `Student ${i + 1}`;
									const meta = [
										`${s.age}`.trim() && `Age ${`${s.age}`.trim()}`,
										s.lessonInterests.map(instrumentDisplayLabel).join(', '),
									]
										.filter(Boolean)
										.join(' · ');
									const n = s.requestedSlotIds.length;
									const hatchOn = (s.returning && s.keepLastYearTime) || s.openToAnything;
									const needsAttn = timesAttn && !childCovered(s);
									const countLabel = n
										? `${n} ${n === 1 ? 'time' : 'times'} selected for ${first} — they'll appear on the review step.`
										: hatchOn
										? "Got it — we'll take it from here on the review step."
										: needsAttn
										? `Pick at least one time for ${first} — or tap "Any of these could work."`
										: 'No times selected yet.';

									return (
										<div
											className={`rg-card rg-formpad${needsAttn ? ' rg-card--attn' : ''}`}
											key={i}
											ref={(el) => (timesCardRefs.current[i] = el)}>
											<span className='accent-bar' />
											<div className='rg-cardhead'>
												<h2 className='rg-cardtitle'>Times for {first}</h2>
												{meta && <span className='rg-cardmeta'>{meta}</span>}
											</div>
											<p className='rg-cardsub'>
												The time we place {first} in is theirs for the whole school
												year. Tap all that could work.
											</p>

											{s.returning && (
												<>
													{hatchButton(
														s.keepLastYearTime,
														() => toggleChildFlag(i, 'keepLastYearTime'),
														"We'd love to keep our time from last year",
														"We'll check it against this fall's schedule first."
													)}
													<div className='rg-divider'>
														<span className='rg-divider__line' />
														<span className='rg-divider__t'>or pick specific times below</span>
														<span className='rg-divider__line' />
													</div>
												</>
											)}

											{slots.length === 0 ? (
												<p className='register-slots-status'>
													Our fall times aren't posted yet — check "Any of these
													could work" below and we'll reach out with options.
												</p>
											) : visibleSlots.length === 0 ? (
												<p className='register-slots-status'>
													No open times at this location right now — try another
													location filter, or check "Any of these could work" below.
												</p>
											) : (
												dayOrder.map((day) => (
													<div className='rg-day' key={day}>
														<div className='rg-dayhead'>
															<span className='rg-dayname'>{getDayName(day)}</span>
															<span className='rg-dayline' />
														</div>
														<div className='rg-slots'>
															{slotsByDay[day].map((slot) => {
																const on = s.requestedSlotIds.includes(slot.id);
																return (
																	<button
																		type='button'
																		key={slot.id}
																		className={`rg-chip${on ? ' rg-chip--on' : ''}`}
																		onClick={() => toggleSlot(i, slot.id)}
																		aria-pressed={on}>
																		<span className='rg-chip__time'>
																			<FontAwesomeIcon
																				icon={on ? faCircleCheckSolid : faCircle}
																			/>
																			{formatTime12Hour(slot.startTime)}
																		</span>
																		<span className='rg-chip__loc'>
																			{slotLocationShort(slot.location)}
																		</span>
																	</button>
																);
															})}
														</div>
													</div>
												))
											)}

											<div className='rg-hatches'>
												{hatchButton(
													s.openToAnything,
													() => toggleChildFlag(i, 'openToAnything'),
													"Any of these could work — we're flexible",
													"Show us everything; we'll suggest the best fit."
												)}
											</div>
											<p
												className={`rg-count${needsAttn ? ' rg-count--attn' : ''}`}
												aria-live='polite'>
												{countLabel}
											</p>
										</div>
									);
								})}

								<div className='rg-card rg-notescard'>
									<label htmlFor='rg-notes' className='rg-noteslabel'>
										Anything about your schedule we should know?{' '}
										<span className='register-opt'>(optional)</span>
									</label>
									<textarea
										id='rg-notes'
										rows={4}
										className='rg-notesinput'
										placeholder='School activities, carpool days, anything that helps us place you…'
										value={scheduleNotes}
										onChange={(e) => setScheduleNotes(e.target.value)}
									/>
								</div>
							</>
						)}

						<div className='rg-actions'>
							<button
								type='button'
								className='btn-cyv-ghost'
								onClick={() => {
									setError('');
									setStep(1);
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}>
								<FontAwesomeIcon icon={faArrowLeft} className='me-2' />
								Back
							</button>
							<div className='rg-continuewrap'>
								{/* Deliberately never disabled — an invalid click gets
								    moment-of-need feedback via goReview (scroll + card
								    marks) instead of a dead button. */}
								<button type='button' className='btn-cyv-teal' onClick={goReview}>
									Continue to review
									<FontAwesomeIcon icon={faArrowRight} className='ms-2' />
								</button>
								<span
									className={`rg-hint${timesAttn && !allOk ? ' rg-hint--attn' : ''}`}
									aria-live='polite'>
									{allOk
										? 'You can adjust everything on the review step.'
										: 'Pick at least one time (or check an option above) to continue.'}
								</span>
							</div>
						</div>
					</section>

					<aside className='rg-aside'>
						<div className='cyv-card rg-sidecard' style={{ '--accent': 'var(--grad-lessons)' }}>
							<span className='accent-bar' />
							<span className='eyebrow rg-sideeyebrow'>School-year voice lessons</span>
							<h3 className='rg-sidetitle'>One weekly lesson, all year</h3>
							{/* Year-first on purpose (Anthony, 2026-08-18): the anchor
							matches the school-year commitment, canon's phrasing rule
							("split across 10 months", never "flat monthly rate") and
							Grace's own quote script — a naive first-time reader took
							the old big "$240/mo" as the whole story. The monthly
							figure stays STRONG (bold, first words of the note), not
							fine print — it is the number families budget with. */}
							<div className='rg-price'>
								<span className='rg-price__amt'>{TUITION_SEASON_TOTAL_LABEL}</span>
								<span className='rg-price__per'>/ school year</span>
							</div>
							<p className='rg-price__note'>
								<strong>
									Split into {TUITION_INSTALLMENTS} monthly payments of{' '}
									{TUITION_MONTHLY_LABEL}
								</strong>
								, {TUITION_SCHEDULE_LABEL} — tax included.
							</p>
							<div className='rg-sidelist'>
								<div className='rg-siderow'>
									<FontAwesomeIcon
										icon={faCalendarCheck}
										className='rg-siderow__i rg-siderow__i--orange'
									/>
									<p>
										<strong>Lessons start {firstFallLessonShort}.</strong> Your weekly
										time runs fall through spring.
									</p>
								</div>
								<div className='rg-siderow'>
									<FontAwesomeIcon icon={faMusic} className='rg-siderow__i rg-siderow__i--teal' />
									<p>
										Every student has one 45-minute voice lesson each week at a set
										time that's theirs all year — building toward our studio recitals.
									</p>
								</div>
								<div className='rg-siderow'>
									<FontAwesomeIcon icon={faStar} className='rg-siderow__i rg-siderow__i--pink' />
									<p>
										We're Color Your Voice — a youth voice &amp; performing arts studio
										in Burnsville, MN.
									</p>
								</div>
							</div>
							<p className='rg-tagline'>Helping young artists find their voice</p>
						</div>
						<div className='rg-askwrap'>
							<p className='rg-ask__lead'>Not ready to pick times?</p>
							<Link to='/contact' className='rg-ask'>
								<FontAwesomeIcon icon={faComment} />
								Just ask us a question
							</Link>
						</div>
					</aside>
				</div>
			</main>
		);
	}

	// ── Review (step 3) ──
	if (step === 3) {
		return (
			<main className='register-page'>
				<Helmet defer={false}>
					<title>Review your registration | Color Your Voice</title>
				</Helmet>
				<div className='register-card'>
					<div className='register-inner'>
						<p className='register-eyebrow'>New student registration</p>
						<h1 className='register-title'>Review &amp; confirm</h1>
						<p className='register-sub'>Take a quick look — you can go back and edit anything.</p>

						{stepsIndicator(3)}

						{error && (
							<Alert variant='danger' className='register-error'>
								{error}
							</Alert>
						)}

						{students.map((s, i) => (
							<div className='register-review-group' key={i}>
								<h3>{students.length > 1 ? `Student ${i + 1}` : 'Student'}</h3>
								<div className='register-review-row'>
									<span className='rk'>Name</span>
									<span>
										{fullName(s.firstName, s.lastName)} (age {s.age})
									</span>
								</div>
								{s.pronouns && (
									<div className='register-review-row'>
										<span className='rk'>Pronouns</span>
										<span>{s.pronouns}</span>
									</div>
								)}
								{s.school && (
									<div className='register-review-row'>
										<span className='rk'>School / grade</span>
										<span>{s.school}</span>
									</div>
								)}
								{s.lessonInterests.length > 0 && (
									<div className='register-review-row'>
										<span className='rk'>Lessons and interest</span>
										<span>{s.lessonInterests.map(instrumentDisplayLabel).join(', ')}</span>
									</div>
								)}
								{s.locationPreference && (
									<div className='register-review-row'>
										<span className='rk'>Location</span>
										<span>{s.locationPreference}</span>
									</div>
								)}
								{s.returning && (
									<div className='register-review-row'>
										<span className='rk'>Returning</span>
										<span>Yes — has taken lessons with CYV before</span>
									</div>
								)}
								<div className='register-review-row'>
									<span className='rk'>Times requested</span>
									<span>
										{s.requestedSlotIds.length > 0 ? (
											<>
												{s.requestedSlotIds
													.map((id) => slots.find((sl) => sl.id === id))
													.filter(Boolean)
													.map((sl) => (
														<span className='register-review-slot' key={sl.id}>
															{describeSlot(sl)}
														</span>
													))}
											</>
										) : (
											'No specific times picked'
										)}
										{s.returning && s.keepLastYearTime && (
											<span className='register-review-slot'>
												Keep last year's time, if available
											</span>
										)}
										{s.openToAnything && (
											<span className='register-review-slot'>Open to anything</span>
										)}
									</span>
								</div>
							</div>
						))}

						<div className='register-review-group'>
							<h3>Parent / guardian</h3>
							<div className='register-review-row'>
								<span className='rk'>Name</span>
								<span>{fullName(parent.firstName, parent.lastName)}</span>
							</div>
							<div className='register-review-row'>
								<span className='rk'>Email</span>
								<span>{parent.email.trim()}</span>
							</div>
							<div className='register-review-row'>
								<span className='rk'>Phone</span>
								<span>{parent.phone.trim()}</span>
							</div>
							{parent.address.trim() && (
								<div className='register-review-row'>
									<span className='rk'>Address</span>
									<span>{parent.address.trim()}</span>
								</div>
							)}
						</div>

						{(referralSource || notes.trim() || scheduleNotes.trim()) && (
							<div className='register-review-group'>
								<h3>A little more</h3>
								{referralSource && (
									<div className='register-review-row'>
										<span className='rk'>Heard about us</span>
										<span>{referralSource}</span>
									</div>
								)}
								{showThank && referredBy.trim() && (
									<div className='register-review-row'>
										<span className='rk'>Who to thank</span>
										<span>{referredBy.trim()}</span>
									</div>
								)}
								{notes.trim() && (
									<div className='register-review-row'>
										<span className='rk'>Notes</span>
										<span>{notes.trim()}</span>
									</div>
								)}
								{scheduleNotes.trim() && (
									<div className='register-review-row'>
										<span className='rk'>Schedule notes</span>
										<span>{scheduleNotes.trim()}</span>
									</div>
								)}
							</div>
						)}

						<p className='register-review-note'>
							There's no payment today — this sends us your request, and we'll confirm
							your lesson time by email. Times aren't booked until we confirm.
						</p>

						<div className='register-actions'>
							<button
								type='button'
								className='btn-cyv-ghost'
								onClick={() => {
									setError('');
									setStep(2);
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}}
								disabled={submitting}>
								<FontAwesomeIcon icon={faArrowLeft} className='me-2' />
								Back
							</button>
							<button
								type='button'
								className='btn-cyv-teal'
								onClick={handleConfirm}
								disabled={submitting}>
								{submitting ? 'Sending…' : 'Send registration request'}
							</button>
						</div>
					</div>
				</div>
			</main>
		);
	}

	// ── Step 1: your info ──
	return (
		<main className='register-page'>
			{/* INDEXABLE since 2026-08-17. The noindex here was correct only while
			    /register was unlinked (uncurated pool — task gTndi4Yuh8mmME0L8fnJ).
			    The 08-08 fall push re-linked it from the Home hero, the Home CTA
			    band, FAQ and Faculty and never dropped the noindex — so for nine
			    days the page the whole marketing site funnels into was telling
			    Google to skip it. Added to public/sitemap.xml in the same change. */}
			<Seo
				title='Register a New Student | Color Your Voice'
				description='Register your young artist with Color Your Voice in Burnsville, MN. Tell us a little about your singer and we will follow up about lessons and openings.'
				canonical={`${SITE}/register`}
			/>
			<div className='register-card'>
				<div className='register-inner'>
					<p className='register-eyebrow'>New student registration</p>
					<h1 className='register-title'>Tell us a little about your young artist.</h1>
					<p className='register-sub'>
						Fill this out and we'll contact you to set up lessons. There's no payment
						today. This just lets us know you're interested.
					</p>

					{stepsIndicator(1)}

					{error && (
						<Alert variant='danger' className='register-error'>
							{error}
						</Alert>
					)}

					<form onSubmit={goPickTimes} noValidate>
						{/* Honeypot — real people never see or fill this. */}
						<div className='register-hp' aria-hidden='true'>
							<label>
								Company website
								<input
									type='text'
									tabIndex={-1}
									autoComplete='off'
									value={companyWebsite}
									onChange={(e) => setCompanyWebsite(e.target.value)}
								/>
							</label>
						</div>

						{students.map((s, i) => (
							<div className='register-student' key={i}>
								<div className='register-student__head'>
									<p className='register-student__title'>
										{students.length > 1 ? `Student ${i + 1}` : 'Student'}
									</p>
									{students.length > 1 && (
										<button
											type='button'
											className='register-remove'
											onClick={() => removeStudent(i)}>
											<FontAwesomeIcon icon={faXmark} className='me-1' />
											Remove
										</button>
									)}
								</div>

								<div className='register-row'>
									<div className='register-field'>
										<label className='register-label'>
											First name <span className='register-req'>*</span>
										</label>
										<input
											className='register-input'
											value={s.firstName}
											onChange={setStudentField(i, 'firstName')}
											autoComplete='off'
										/>
									</div>
									<div className='register-field'>
										<label className='register-label'>
											Last name <span className='register-req'>*</span>
										</label>
										<input
											className='register-input'
											value={s.lastName}
											onChange={setStudentField(i, 'lastName')}
											autoComplete='off'
										/>
									</div>
								</div>

								<div className='register-row'>
									<div className='register-field'>
										<label className='register-label'>
											Age <span className='register-req'>*</span>
										</label>
										<select
											className='register-select'
											value={s.underEight ? 'under8' : `${s.age}`}
											onChange={setAgeChoice(i)}>
											<option value=''>Select…</option>
											{AGE_OPTIONS.map((a) => (
												<option key={a} value={a}>
													{a}
												</option>
											))}
											<option value='under8'>Under 8</option>
										</select>
									</div>
									{selectField('Pronouns', s.pronouns, setStudentField(i, 'pronouns'), PRONOUNS)}
								</div>

								{s.underEight && (
									<div className='register-field register-under8'>
										<label className='register-label'>
											Their age <span className='register-req'>*</span>
										</label>
										<input
											className='register-input'
											type='number'
											min='6'
											max='7'
											inputMode='numeric'
											value={s.age}
											onChange={setStudentField(i, 'age')}
										/>
										<p className='register-under8__hint'>
											Voice lessons usually start at 8. For younger students we place
											case-by-case — enter their age and we'll talk it through with
											you.
										</p>
									</div>
								)}

								<div className='register-field'>
									<label className='register-label'>
										Current school / grade <span className='register-opt'>(optional)</span>
									</label>
									<input
										className='register-input'
										value={s.school}
										onChange={setStudentField(i, 'school')}
										autoComplete='off'
									/>
								</div>

								<fieldset className='register-field register-interests'>
									<legend className='register-label'>
										Lessons and interest:{' '}
										<span className='register-opt'>
											Choose as many as you're interested in.
										</span>
									</legend>
									{LESSON_INTERESTS.map((o) => (
										<label className='register-check' key={o}>
											<input
												type='checkbox'
												checked={s.lessonInterests.includes(o)}
												onChange={() => toggleInterest(i, o)}
											/>
											<span>{instrumentDisplayLabel(o)}</span>
										</label>
									))}
								</fieldset>

								{selectField(
									'Location preference',
									s.locationPreference,
									setStudentField(i, 'locationPreference'),
									LOCATIONS
								)}

								<label className='register-check'>
									<input
										type='checkbox'
										checked={s.returning}
										onChange={() => toggleReturning(i)}
									/>
									<span>
										{students.length > 1 ? 'This student has' : 'My young artist has'}{' '}
										taken lessons with CYV before
									</span>
								</label>
							</div>
						))}

						<button type='button' className='btn-cyv-ghost register-add' onClick={addStudent}>
							<FontAwesomeIcon icon={faPlus} className='me-2' />
							Add another student
						</button>

						<h2 className='register-section-title'>Parent / guardian</h2>
						<div className='register-row'>
							<div className='register-field'>
								<label className='register-label'>
									First name <span className='register-req'>*</span>
								</label>
								<input className='register-input' value={parent.firstName} onChange={setP('firstName')} />
							</div>
							<div className='register-field'>
								<label className='register-label'>
									Last name <span className='register-req'>*</span>
								</label>
								<input className='register-input' value={parent.lastName} onChange={setP('lastName')} />
							</div>
						</div>
						<div className='register-row'>
							<div className='register-field'>
								<label className='register-label'>
									Email <span className='register-req'>*</span>
								</label>
								<input
									className='register-input'
									type='email'
									value={parent.email}
									onChange={setP('email')}
									autoComplete='email'
								/>
							</div>
							<div className='register-field'>
								<label className='register-label'>
									Phone <span className='register-req'>*</span>
								</label>
								<input
									className='register-input'
									type='tel'
									value={parent.phone}
									onChange={setP('phone')}
									autoComplete='tel'
								/>
							</div>
						</div>
						<div className='register-field'>
							<label className='register-label'>
								Address <span className='register-opt'>(optional)</span>
							</label>
							<input className='register-input' value={parent.address} onChange={setP('address')} />
						</div>

						<h2 className='register-section-title'>A little more</h2>
						<div className='register-field'>
							<label className='register-label'>
								How did you hear about us? <span className='register-opt'>(optional)</span>
							</label>
							<select
								className='register-select'
								value={referralSource}
								onChange={(e) => setReferralSource(e.target.value)}>
								<option value=''>Select…</option>
								{HEARD_OPTIONS.map((o) => (
									<option key={o} value={o}>
										{o}
									</option>
								))}
							</select>
						</div>
						{showThank && (
							<div className='register-field'>
								<label className='register-label'>
									Who should we thank? <span className='register-opt'>(optional)</span>
								</label>
								<input
									className='register-input'
									value={referredBy}
									onChange={(e) => setReferredBy(e.target.value)}
									placeholder='A name, so we can say thanks'
								/>
							</div>
						)}
						<div className='register-field'>
							<label className='register-label'>
								Anything else? <span className='register-opt'>(optional)</span>
							</label>
							<textarea
								className='register-textarea'
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Goals, questions, scheduling notes — anything you'd like us to know."
							/>
						</div>

						<label className='register-policy'>
							<input
								type='checkbox'
								checked={acknowledged}
								onChange={(e) => setAcknowledged(e.target.checked)}
							/>
							<span>
								I've read the{' '}
								<Link to='/policies' target='_blank' rel='noreferrer'>
									studio policies
								</Link>{' '}
								and understand a team member will reach out about lessons.
							</span>
						</label>

						<div className='register-actions'>
							<button type='submit' className='btn-cyv-teal' disabled={submitting}>
								{submitting ? 'Saving…' : 'Continue'}
								{!submitting && <FontAwesomeIcon icon={faArrowRight} className='ms-2' />}
							</button>
						</div>
					</form>
				</div>
			</div>
		</main>
	);
};

export default RegisterStudent;
